import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, BadgeEuro, Building2, Handshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const services = [
  {
    icon: BadgeEuro,
    overline: "Financements",
    title: "Certificats d'Économies d'Énergie (CEE)",
    intro: "Les CEE sont un levier de financement majeur, encore trop peu connu par les entreprises.",
    points: [
      "Identification des opérations éligibles (fiches standardisées et opérations spécifiques)",
      "Montage complet des dossiers et sécurisation réglementaire",
      "Valorisation de vos CEE auprès des obligataires.",
      "Optimisation du couplage CEE avec les autres aides publiques",
    ],
    testid: "service-block-cee",
  },
  {
    icon: Building2,
    overline: "Travaux",
    title: "Rénovation énergétique",
    intro: "Une rénovation réussie repose sur un scénario de travaux cohérent, chiffré et séquencé. Nous le construisons avec vous, puis en supervisons l'exécution.",
    points: [
      "Audit énergétique approfondi de vos bâtiments et de vos usages",
      "Scénarios de travaux : Chauffage, isolation, récupération et régulation de chaleur sur groupe de froid.",
      "Accompagnement administratif",
    ],
    testid: "service-block-renovation",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-4">Nos services</p>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight text-white leading-[1.15]">
              Un conseil d'excellence, <span className="gold-text">de l'étude au résultat</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300 font-light">
              Levi One intervient comme votre interlocuteur privilégié : nous analysons,
              structurons, finançons et présentons nos partenaires.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 space-y-8">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={0.05}>
            <div
              data-testid={s.testid}
              className="grid lg:grid-cols-[1fr_1.2fr] gap-10 bg-navy-800/50 border border-gold/15 rounded-3xl p-8 sm:p-12 hover:border-gold/40 transition-colors duration-300"
            >
              <div>
                <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-6">
                  <s.icon size={26} className="text-gold-bright" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-2">{s.overline}</p>
                <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white leading-snug">{s.title}</h2>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">{s.intro}</p>
              </div>
              <ul className="space-y-4 self-center">
                {s.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-slate-300 text-sm sm:text-base leading-relaxed">
                    <span className="mt-1 w-6 h-6 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                      <Check size={13} className="text-gold-bright" />
                    </span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-navy-900 border-y border-gold/10" data-testid="services-partners-block">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-6">
              <Handshake size={26} className="text-gold-bright" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold mb-3">Mise en relation</p>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white leading-snug">
              Nous vous présentons des partenaires de qualité — et nous restons à vos côtés
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              Levi One vous met en relation avec des partenaires de confiance, sélectionnés pour leur
              expertise et leur capacité à répondre aux exigences de votre projet. Bureaux d'études,
              installateurs RGE, financeurs ou autres acteurs spécialisés : nous privilégions des
              partenaires reconnus et adaptés à vos besoins.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Notre rôle ne s'arrête pas à la mise en relation. Nous vous accompagnons tout au long du
              projet, de la sélection du partenaire jusqu'au suivi de sa réalisation, afin de vous
              garantir une relation claire, fluide et sereine.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Vous bénéficiez ainsi d'un réseau de partenaires de qualité, tout en gardant à vos côtés
              un interlocuteur indépendant pour vous guider et défendre vos intérêts.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-2xl overflow-hidden border border-gold/20 shadow-2xl shadow-black/40">
              <img
                src="https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxzb2xhciUyMGJ1aWxkaW5nJTIwcmVub3ZhdGlvbiUyMGVuZXJneSUyMGVuZ2luZWVyJTIwdGVhbXxlbnwwfHx8fDE3ODgyOTkyMDZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Installation solaire en rénovation énergétique"
                className="w-full h-[360px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-center">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-white">
            Un projet, une question de conformité ?
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">
            Parlez-nous de votre situation : un conseiller Levi One vous répond sous 24 h ouvrées.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-bright hover:from-gold-bright hover:to-gold text-navy-950 font-semibold px-8 py-4 rounded-full shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5"
          >
            Demander une étude <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
